/**
 * Subscription & Payment Service
 * Handles subscription management and payment processing
 */

import { authenticatedFetch } from './authService.js';
import { publicConfig } from '../state/publicConfig.js';

function resolveApiBase() {
  let base = window.BATEMO_API?.baseUrl !== undefined ? window.BATEMO_API.baseUrl : '/api/v1';
  if (window.location.protocol === 'https:' && typeof base === 'string' && base.startsWith('http://')) {
    base = base.replace('http://', 'https://');
  }
  return base;
}

const API_BASE = {
  toString: () => resolveApiBase(),
  valueOf: () => resolveApiBase()
};

/**
 * Parse FastAPI error detail into a human-readable string.
 * FastAPI validation errors return detail as an array of objects:
 *   [{ loc: ["body", "field"], msg: "...", type: "..." }]
 * This converts them to a readable message.
 */
function parseApiError(detail, fallback = 'An error occurred') {
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const msgs = detail.map(e => {
      const field = e.loc?.filter(l => l !== 'body').join('.') || '';
      const msg = e.msg || '';
      return field ? `${field}: ${msg}` : msg;
    }).filter(Boolean);
    return msgs.length > 0 ? msgs.join('; ') : fallback;
  }
  if (typeof detail === 'object' && detail.msg) return detail.msg;
  return fallback;
}

// Cached pricing from Stripe
let _cachedPricing = null;

/**
 * Fetch current pricing from backend (Stripe-backed, cached).
 * Public endpoint, no auth required.
 *
 * @returns {Promise<{ yearly_price: number, yearly_price_cents: number, currency: string }>}
 */
export async function getPricing() {
  if (_cachedPricing) return _cachedPricing;
  try {
    const response = await fetch(`${API_BASE}/payments/pricing`);
    if (response.ok) {
      _cachedPricing = await response.json();
      return _cachedPricing;
    }
  } catch (e) {
    console.warn('Failed to fetch pricing, using fallback:', e);
  }
  // Fallback
  return { yearly_price: 4900, yearly_price_cents: 490000, currency: 'EUR' };
}

/**
 * Get current user's subscription
 */
export async function getSubscription() {
  const response = await authenticatedFetch(`${API_BASE}/payments/subscription`);
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch subscription');
  }

  return response.json();
}

/**
 * Request access to a subscription tier (DEMO or PRO)
 *
 * For DEMO: Returns { status: 'requested', message: '...' }
 * For PRO: Returns { status: 'payment_required', client_secret: '...', payment_intent_id: '...', amount: 1.00 }
 *
 * @param {string} tier - 'DEMO' or 'PRO'
 * @param {string} [promoCode] - Optional promo code for discount
 * @param {object} [billingData] - Optional billing details for VAT calculation
 * @returns {Promise<object>} Response with status and details
 */
export async function requestAccess(tier, promoCode = null, billingData = null) {
  const body = { tier };
  if (promoCode) {
    body.promo_code = promoCode;
  }
  if (billingData) {
    body.billing_data = billingData;
  }

  const response = await authenticatedFetch(`${API_BASE}/payments/request-access`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(parseApiError(error.detail, 'Failed to request access'));
  }

  return response.json();
}

/**
 * Validate a promo code
 *
 * Returns discount info if valid, error if invalid
 *
 * @param {string} code - The promo code to validate
 * @returns {Promise<object>} Response with valid, discount_percent, code, message
 */
export async function validatePromoCode(code) {
  const response = await authenticatedFetch(`${API_BASE}/payments/validate-promo-code`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Invalid promo code');
  }

  return response.json();
}

/**
 * Request a quote for PRO subscription (B2B flow for business customers)
 * 
 * Creates a Stripe Quote that can be sent to the customer for approval.
 * Quote includes company details, tax ID, and billing address.
 * 
 * @param {object} quoteData - Quote request data
 * @param {string} quoteData.company_name - Company name for invoice
 * @param {string} quoteData.company_address - Full address (street, city, postal code)
 * @param {string} quoteData.company_country - Country code (e.g., 'DE', 'AT', 'CH')
 * @param {string} quoteData.tax_id - VAT/Tax ID (e.g., 'DE123456789')
 * @param {string} [quoteData.contact_email] - Contact email (defaults to user email)
 * @param {string} [quoteData.notes] - Additional notes for the quote
 * @param {string} [quoteData.lead_source] - Optional attribution tag (e.g.
 *   'demo_limit_modal') the backend records as a sales_inquiry conversion
 *   event. Omitted from the POST body when undefined (JSON.stringify drops it).
 * @returns {Promise<object>} Response with quote_id, quote_url, expires_at
 */
export async function requestQuote(quoteData) {
  // Passes the payload through verbatim, so any optional field the caller
  // includes (e.g. lead_source) reaches the backend; undefined keys are
  // dropped by JSON.stringify, keeping the field truly optional.
  const response = await authenticatedFetch(`${API_BASE}/payments/request-quote`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(quoteData)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(parseApiError(error.detail, 'Failed to request quote'));
  }

  return response.json();
}

/**
 * Submit a lightweight demo "Talk to our team" contact inquiry.
 *
 * Backs the slim /contact page (name/email/message, no billing). The backend
 * records the SAME `sales_inquiry` conversion event (with `lead_source`) as the
 * B2B quote flow, so the conversion funnel stays intact.
 *
 * @param {object} inquiry
 * @param {string} inquiry.name - Contact name
 * @param {string} inquiry.email - Contact email
 * @param {string} inquiry.message - Message to the sales team
 * @param {string} [inquiry.company] - Optional company name
 * @param {string} [inquiry.lead_source] - Optional attribution tag (e.g.
 *   'demo_limit_modal'); undefined keys are dropped by JSON.stringify.
 * @returns {Promise<{ok: boolean}>}
 */
export async function submitContactInquiry(inquiry) {
  const response = await authenticatedFetch(`${API_BASE}/payments/contact`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(inquiry),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(parseApiError(error.detail, 'Failed to send your message'));
  }

  return response.json();
}

/**
 * Accept a quote (finalize and pay)
 *
 * @param {string} quoteId - The Stripe Quote ID to accept
 * @returns {Promise<object>} Response with payment_intent details
 */
export async function acceptQuote(quoteId) {
  const response = await authenticatedFetch(`${API_BASE}/payments/accept-quote/${quoteId}`, {
    method: 'POST'
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to accept quote');
  }

  return response.json();
}

/**
 * Get user's quotes (pending and accepted)
 * 
 * @returns {Promise<object>} Response with quotes array
 */
export async function getQuotes() {
  const response = await authenticatedFetch(`${API_BASE}/payments/quotes`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch quotes');
  }

  return response.json();
}

/**
 * Confirm payment intent was completed (called after Stripe Elements payment)
 * 
 * @param {string} paymentIntentId - The Stripe Payment Intent ID
 * @returns {Promise<object>} Response with status
 */
export async function confirmPaymentIntent(paymentIntentId) {
  const response = await authenticatedFetch(`${API_BASE}/payments/confirm-payment-intent?payment_intent_id=${encodeURIComponent(paymentIntentId)}`, {
    method: 'POST'
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to confirm payment');
  }

  return response.json();
}

/**
 * Cancel the current user's pending payment intent and revoke associated promo code.
 *
 * @returns {Promise<object>} Response with status and message
 */
export async function cancelPaymentIntent() {
  const response = await authenticatedFetch(`${API_BASE}/payments/cancel-payment-intent`, { method: 'POST' });
  if (!response.ok) {
    const e = await response.json().catch(() => ({}));
    throw new Error(parseApiError(e.detail, 'Failed to cancel payment intent'));
  }
  return response.json();
}

/**
 * Create Stripe checkout session (deprecated - use requestAccess instead)
 * @deprecated Use requestAccess() for new implementation
 */
export async function createCheckoutSession(tier, successUrl, cancelUrl) {
  // Redirect to new flow
  console.warn('createCheckoutSession is deprecated. Use requestAccess() instead.');
  return requestAccess(tier);
}

/**
 * Create a Stripe Customer Portal session for managing subscription
 * (update payment methods, view Stripe billing history)
 *
 * @returns {Promise<{ url: string }>} Portal session URL
 */
export async function createCustomerPortalSession() {
  const response = await authenticatedFetch(`${API_BASE}/payments/customer-portal`, {
    method: 'POST',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to create portal session');
  }

  return response.json();
}

/**
 * Cancel subscription (unified for both CARD and B2B).
 * Requires 90+ days notice before period end per T&C §8.
 *
 * @param {string} [reason] - Optional cancellation reason
 * @returns {Promise<object>} { current_period_end, message }
 */
export async function cancelSubscription(reason = null) {
  const options = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason })
  };
  const response = await authenticatedFetch(`${API_BASE}/payments/cancel-subscription`, options);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to cancel subscription');
  }

  return response.json();
}

/**
 * @deprecated Use cancelSubscription() instead — unified endpoint handles both CARD and B2B.
 */
export async function cancelB2BSubscription(reason = null) {
  return cancelSubscription(reason);
}

/**
 * Reactivate a subscription that was set to cancel at period end.
 * Reverses the cancellation and resumes auto-renewal.
 *
 * @returns {Promise<object>} { status, current_period_end, message }
 */
export async function reactivateSubscription() {
  const response = await authenticatedFetch(`${API_BASE}/payments/reactivate-subscription`, {
    method: 'POST'
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to reactivate subscription');
  }

  return response.json();
}

/**
 * Get payment history
 */
export async function getPaymentHistory(skip = 0, limit = 100) {
  const response = await authenticatedFetch(
    `${API_BASE}/payments/payment-history?skip=${skip}&limit=${limit}`
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch payment history');
  }

  return response.json();
}

/**
 * Get user's invoices (custom-generated, from our Invoice table)
 *
 * @param {number} limit - Maximum number of invoices to retrieve
 * @returns {Promise<{ invoices: Array }>} Response with invoices array
 */
export async function getInvoices(limit = 100) {
  const response = await authenticatedFetch(`${API_BASE}/payments/invoices?limit=${limit}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch invoices');
  }

  return response.json();
}

/**
 * Download custom invoice PDF with Batemo footer
 * 
 * @param {string} invoiceId - Stripe invoice ID (starts with 'in_')
 * @returns {Promise<Blob>} PDF file as blob
 */
export async function downloadInvoice(invoiceId) {
  const response = await authenticatedFetch(`${API_BASE}/payments/custom-invoice-pdf/${invoiceId}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to download invoice');
  }

  return response.blob();
}

/**
 * Invoice status labels for display
 */
export const INVOICE_STATUS_LABELS = {
  draft: 'Draft',
  open: 'Open',
  paid: 'Paid',
  void: 'Void',
  uncollectible: 'Uncollectible'
};

/**
 * Get billing address for invoices
 * 
 * @returns {Promise<object>} Billing address details
 */
export async function getBillingAddress() {
  const response = await authenticatedFetch(`${API_BASE}/payments/billing-address`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch billing address');
  }

  return response.json();
}

/**
 * Update billing address for future invoices
 *
 * @param {object} data - Billing address fields (flat structure)
 * @param {string} data.company_name - Company name
 * @param {string} data.street - Street address
 * @param {string} data.city - City
 * @param {string} data.postal_code - Postal/ZIP code
 * @param {string} data.country - Country code (e.g., 'DE')
 * @param {string} data.tax_id - VAT/Tax ID (optional)
 * @param {string} data.tax_id_type - Tax ID type (optional)
 * @returns {Promise<object>} Updated billing address
 */
export async function updateBillingAddress(data) {
  const response = await authenticatedFetch(`${API_BASE}/payments/billing-address`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to update billing address');
  }

  return response.json();
}

/**
 * Admin: Get pending subscriptions
 * @deprecated Use adminService.js getSubscriptions({ approval_status: 'PENDING' }) for unified admin API
 */
export async function getPendingSubscriptions() {
  // Use correct admin endpoint path
  const response = await authenticatedFetch(`${API_BASE}/admin/subscriptions/pending`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch pending subscriptions');
  }

  return response.json();
}

/**
 * Admin: Approve subscription
 * Uses internal user_id for admin operations
 */
export async function approveSubscription(userId) {
  const response = await authenticatedFetch(
    `${API_BASE}/admin/subscriptions/${userId}/approve`,
    { method: 'POST' }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to approve subscription');
  }

  return response.json();
}

/**
 * Admin: Decline subscription
 * Uses internal user_id for admin operations
 */
export async function declineSubscription(userId, reason = null) {
  const url = reason 
    ? `${API_BASE}/admin/subscriptions/${userId}/decline?reason=${encodeURIComponent(reason)}`
    : `${API_BASE}/admin/subscriptions/${userId}/decline`;
    
  const response = await authenticatedFetch(url, { method: 'POST' });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to decline subscription');
  }

  return response.json();
}

/**
 * Subscription tier details
 * Note: cellCount is fetched dynamically from backend - see getCellCount()
 */
export const SUBSCRIPTION_TIERS = {
  FREE: {
    name: 'Free',
    price: 0,
    order: 0,
    features: [
      'No access to cell data',
      'Basic app navigation',
    ]
  },
  DEMO: {
    name: 'Demo Access',
    price: 0,
    order: 1,
    requiresApproval: true,
    // period + features are dynamic so demo length stays in sync with
    // settings.demo_duration_days (served via /api/v1/config/public).
    get period() { return `${publicConfig.value.demo_duration_days} days`; },
    get features() {
      const days = publicConfig.value.demo_duration_days;
      const dailyLimit = publicConfig.value.demo_cell_view_daily_limit;
      // "No credit card required" and "N-day free trial" described the old
      // self-service signup. Demo access now starts with a booked meeting
      // (/request-demo) and the account is created by us afterwards — a card
      // still promising an instant trial contradicts its own button.
      return [
        'Full catalog access (all cells)',
        `${dailyLimit} cell deep dives per day`,
        'All analysis features',
        `${days} days of access, free`,
        '30-min intro call with an engineer',
      ];
    },
  },
  PRO: {
    name: 'PRO Access',
    get price() { return _cachedPricing?.yearly_price ?? 4900; }, // Dynamic from Stripe
    regularPrice: 5900,    // €5,900/year regular price (static marketing value)
    period: 'year',
    order: 2,
    requiresApproval: true,
    // Features are generated dynamically with real cell count
    // Round cellCount-1 to next 10 multiple (e.g., 271 -> 270 -> 260)
    getFeatures: (cellCount) => {
      const roundedCount = cellCount ? Math.floor((cellCount - 1) / 10) * 10 : 250;
      return [
        { text: `Always up to date — every new cell, the day it's measured`, hasDownload: true },
        'Unlimited detailed cell views',
        `Full catalog access (${roundedCount}+ cells)`,
        'All analysis features',
        'Annual subscription',
        'Credit card / Request Quote'
      ];
    },
    // Static features - use placeholder for backwards compatibility
    // NOTE: Use getFeatures(cellCount) whenever possible for accurate count
    features: [
      `Always up to date — every new cell, the day it's measured`,
      'Unlimited detailed cell views',
      'Full catalog access',
      'All analysis features',
      'Annual subscription',
      'Credit card / Request Quote'
    ]
  }
};

/**
 * Subscription status labels
 */
export const SUBSCRIPTION_STATUS_LABELS = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
  CANCELLED: 'Cancelled',
  PAST_DUE: 'Past Due',
  TRIALING: 'Trial Period',
  QUOTE_PENDING: 'Quote Pending'  // New status for B2B quote flow
};

/**
 * Approval status labels (user-facing: "Processing" instead of "Pending")
 */
export const APPROVAL_STATUS_LABELS = {
  NONE: 'No Plan Selected',
  PENDING: 'Processing',  // User sees "Processing", not "Pending Approval"
  APPROVED: 'Approved',
  DECLINED: 'Request Declined',
  QUOTE_SENT: 'Quote Sent',  // New status for B2B quote flow
  QUOTE_ACCEPTED: 'Quote Accepted'  // New status for B2B quote flow
};

/**
 * Get user-facing status message based on subscription state
 */
export function getStatusMessage(subscription) {
  if (!subscription) return 'No subscription found';
  
  const { approval_status, status, tier } = subscription;
  
  if (approval_status === 'PENDING') {
    return 'Your access request is being processed. You will be notified once approved.';
  }
  
  if (approval_status === 'QUOTE_SENT') {
    return 'Your quote has been sent. Please review and accept it to proceed with payment.';
  }
  
  if (approval_status === 'QUOTE_ACCEPTED') {
    return 'Your quote has been accepted. Awaiting final approval.';
  }
  
  if (approval_status === 'DECLINED') {
    return 'Your access request was not approved. Please contact support for more information.';
  }
  
  if (approval_status === 'APPROVED') {
    if (status === 'TRIALING') {
      return `Your ${tier} trial is active.`;
    }
    if (status === 'ACTIVE') {
      return `Your ${tier} subscription is active.`;
    }
  }
  
  return SUBSCRIPTION_STATUS_LABELS[status] || status;
}

// Cache for cell count/list
let cachedCellInfo = null;

/**
 * Get cell count from public endpoint
 * Used for dynamic "Access to >X cells" display on Plans page
 * Returns { count } where count is the total number of cells
 * 
 * Note: Uses public endpoint - no authentication required
 * Rate limited to 10/minute per IP
 */
export async function getCellInfo() {
  if (cachedCellInfo) return cachedCellInfo;
  
  try {
    // Fetch from public cell-count endpoint (no auth required, lightweight)
    const response = await fetch(`${API_BASE}/products/cell-count`);
    
    if (!response.ok) {
      console.warn('[subscriptionService] Failed to fetch cell count:', response.status);
      return { count: null };
    }
    
    const data = await response.json();
    if (data && typeof data.count === 'number') {
      cachedCellInfo = { count: data.count };
      console.log(`[subscriptionService] Cell count loaded: ${cachedCellInfo.count} cells`);
      return cachedCellInfo;
    }
    
    console.warn('[subscriptionService] Invalid response format');
    return { count: null };
  } catch (error) {
    console.warn('[subscriptionService] Failed to fetch cell count:', error);
    // Return fallback value - frontend will show "270+" as placeholder
    return { count: null };
  }
}

/**
 * Clear cached cell info (call when cells are added/removed)
 */
export function clearCellInfoCache() {
  cachedCellInfo = null;
}
